import React, { useCallback, useContext, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import { GlobalAppContext } from '../../../context/GlobalContext';
import Request from '../../../shared/request/Request';
import ToasterHelper from '../../../shared/helpers/toaster.helper';
import TreeNodeHelper from '../../../shared/helpers/tree-node.helper';
import { GRID } from '../../../shared/constants/Grid';
import { API } from '../../../shared/constants/API';
import Redirect from '../../redirect/redirect';
import { getItem } from '../../../services/session-storage.service';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator';
import 'primereact/resources/primereact.css';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import { HttpStatusCode } from '../../../shared/request/HttpStatusCode';
import { Dialog } from 'primereact/dialog';
import 'primeicons/primeicons.css';
import { TreeNode } from 'primereact/treenode';
import { TreeTableSelectionKeysType } from 'primereact/treetable';
import InterestsTreeTable from './InterestsTreeTable';
import { InputTextarea } from 'primereact/inputtextarea';
import ActionsTemplate from './actions-template';

const BackOfficeNewsletters = () => {
  const { user } = useContext(GlobalAppContext);
  const createdAtDateTemplate = row => {
    return dayjs(row.createdAt).format('YYYY-MM-DD');
  };

  const sentAtDateTemplate = row => {
    return row.sentAt ? dayjs(row.sentAt).format('YYYY-MM-DD') : '';
  };

  const statusTemplate = row => {
    return row.sentAt ? 'Sent' : 'Draft';
  };

  const actionsTemplate = row => {
    return (
      <ActionsTemplate
        row={row}
        user={user}
        onClickGenerateNewsletter={onClickGenerateNewsletter}
        onClickDelete={onClickDelete}
        onClickEdit={onClickEdit}
        onClickSendNewsletter={onClickSendNewsletter}
      />
    );
  };

  const token = getItem('token');
  const { t } = useTranslation();
  const [loaded, setLoaded] = React.useState(false);
  const [rowData, setRowData] = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(0);
  const [newsletterNameSearch, setNewsletterNameSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(GRID.pageSize);
  const [validated, setValidated] = useState(false);

  const [isNewsLetterModalVisible, setIsNewsLetterModalVisible] = useState(false);
  const [selectedNodeKeys, setSelectedNodeKeys] = useState<TreeTableSelectionKeysType | string>(null);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const [newsletterId, setNewsletterId] = useState(0);
  const [newsletterName, setNewsletterName] = useState('');
  const [initialNodes, setInitialNodes] = useState<TreeNode[] | undefined>(undefined);
  const [newsletterText, setNewsletterText] = useState('');

  const columns = [
    { field: 'newsLetterId', header: 'Id', width: '0%', hidden: true },
    { field: 'name', header: 'Name', width: '40%' },
    { field: 'text', header: 'Text', width: '20%', hidden: true },
    { field: 'createdAt', header: 'Created Date', width: '10%', body: createdAtDateTemplate },
    { field: '', header: 'Status', width: '8%', body: statusTemplate },
    { field: 'sentAt', header: 'Sent Date', width: '8%', body: sentAtDateTemplate },
    { field: '', header: 'Actions', width: '15%', headerStyle: { textAlign: 'center' }, body: actionsTemplate }
  ];

  var years = [];
  var date = new Date();
  const [year, setYear] = useState(date.getFullYear().toString());
  const startingYear = 1930;
  for (var i = date.getFullYear(); i >= startingYear; i--) {
    years.push(i.toString());
  }

  const loadData = useCallback(
    async (
      pageNum: number = pageNumber,
      pageSizing: number = pageSize,
      nameToSearch: string = newsletterNameSearch,
      yearToSearch: string = year
    ) => {
      Request.get(
        `${API.BACK_OFFICE_SERVICE}/getNewsLetters?pageNumber=${pageNum}&pageSize=${pageSizing}&name=${nameToSearch}&year=${yearToSearch}`,
        {}
      )
        .then((response: any) => {
          if (response.status.toString() === HttpStatusCode.OK) {
            setRowData(response.body.newsletters);
            setTotalRows(response.body.total);
          } else {
            if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
              ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
            }
            return [];
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
          return [];
        });
    },
    [newsletterNameSearch, t, year, pageNumber, pageSize]
  );

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      loadData();
    }
  }, [loadData, loaded]);

  const validateNameSearch = (event: any) => {
    let search = '';
    if (event.target.value.length >= 2) {
      setNewsletterNameSearch(event.target.value);
      search = event.target.value;
    }
    loadData(pageNumber, pageSize, search, year);
    event.preventDefault();
  };

  const validateName = (event: any) => {
    setNewsletterName(event.target.value);
    event.preventDefault();
  };

  const changeYear = (event: any) => {
    setYear(event.target.value);
    loadData(pageNumber, pageSize, newsletterNameSearch, event.target.value);
  };

  const onPageChange = (rowData: any) => {
    setPageSize(rowData.rows);
    setPageNumber(rowData.page + 1);
    loadData(rowData.page + 1, rowData.rows, newsletterNameSearch, year);
  };

  const openNewsLetterModal = () => {
    setSelectedNodeKeys(null);
    setInitialNodes(undefined);
    setIsNewsLetterModalVisible(true);
  };

  const onCreateNewsLetter = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    const nodesKeys = selectedNodeKeys != null ? Object.keys(selectedNodeKeys) : [];

    if (form.checkValidity() === true) {
      if (
        !newsletterName ||
        newsletterName.length < 2 ||
        !newsletterText ||
        newsletterText.length < 2 ||
        !selectedNodeKeys ||
        nodesKeys.length === 0
      ) {
        ToasterHelper.notify(
          t('BACK_OFFICE.NEWSLETTERS.VALIDATIONS.NEWSLETTER_NAME_REQUIRED') +
            '\n' +
            t('BACK_OFFICE.NEWSLETTERS.VALIDATIONS.NEWSLETTER_TEXT_REQUIRED') +
            '\n' +
            t('BACK_OFFICE.NEWSLETTERS.VALIDATIONS.INTERESTS_REQUIRED'),
          'warn'
        );
        return;
      }

      Request.post(`${API.BACK_OFFICE_SERVICE}/createNewsLetter`, {
        body: {
          name: newsletterName,
          text: newsletterText,
          interests: nodesKeys
        }
      })
        .then((response: any) => {
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          } else if (response.status.toString() !== HttpStatusCode.OK) {
            ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          } else {
            setIsNewsLetterModalVisible(false);
            setNewsletterId(0);
            setNewsletterName('');
            setNewsletterText('');
            setInitialNodes(undefined);
            ToasterHelper.notify(t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_CREATED_SUCCESSFULLY'), 'success');
            loadData();
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
          return [];
        });
    }
    setValidated(true);
  };

  const onUpdateNewsLetter = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    const nodesKeys = selectedNodeKeys != null ? Object.keys(selectedNodeKeys) : [];

    if (form.checkValidity() === true) {
      if (
        !newsletterName ||
        newsletterName.length < 2 ||
        !newsletterText ||
        newsletterText.length < 2 ||
        !selectedNodeKeys ||
        nodesKeys.length === 0
      ) {
        ToasterHelper.notify(
          t('BACK_OFFICE.NEWSLETTERS.VALIDATIONS.NEWSLETTER_NAME_REQUIRED') +
            '\n' +
            t('BACK_OFFICE.NEWSLETTERS.VALIDATIONS.NEWSLETTER_TEXT_REQUIRED') +
            '\n' +
            t('BACK_OFFICE.NEWSLETTERS.VALIDATIONS.INTERESTS_REQUIRED'),
          'warn'
        );
        return;
      }

      Request.put(`${API.BACK_OFFICE_SERVICE}/updateNewsLetter`, {
        body: {
          id: newsletterId,
          name: newsletterName,
          text: DOMPurify.sanitize(newsletterText),
          interests: nodesKeys
        }
      })
        .then((response: any) => {
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          } else if (response.status.toString() !== HttpStatusCode.OK) {
            ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          } else {
            setIsNewsLetterModalVisible(false);
            setSelectedNodeKeys(null);
            setNewsletterId(0);
            setNewsletterName('');
            setNewsletterText('');
            setInitialNodes(undefined);
            ToasterHelper.notify(t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_UPDATED_SUCCESSFULLY'), 'success');
            loadData();
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
          return [];
        });
    }
    setValidated(true);
  };

  const onClickSendNewsletter = (id: number) => {
    if (window.confirm(t('BACK_OFFICE.NEWSLETTERS.SEND_NEWSLETTER_CONFIRMATION'))) {
      Request.post(`${API.BACK_OFFICE_SERVICE}/sendNewsletter`, {
        body: {
          newsletterId: id
        }
      })
        .then((response: any) => {
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          } else if (response.status.toString() !== HttpStatusCode.OK) {
            ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          } else {
            loadData(pageNumber, pageSize, '', year);
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
        });
    }
  };

  const onClickDelete = (id: number) => {
    if (window.confirm(t('BACK_OFFICE.NEWSLETTERS.DELETE_NEWSLETTER_CONFIRMATION'))) {
      Request.delete(`${API.BACK_OFFICE_SERVICE}/deleteNewsletterById?id=${id}`, {})
        .then((response: any) => {
          if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          } else if (response.status.toString() !== HttpStatusCode.OK) {
            ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          } else {
            loadData(pageNumber, pageSize, '', year);
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
        });
    }
  };

  const onClickEdit = (id: number) => {
    setSubmitButtonDisabled(true);
    Request.get(`${API.BACK_OFFICE_SERVICE}/getNewsletterById?id=${id}`)
      .then((response: any) => {
        if (response.status.toString() === HttpStatusCode.OK) {
          setNewsletterId(response.body.id);
          setNewsletterName(response.body.name);
          setNewsletterText(DOMPurify.sanitize(response.body.text));
          if (response.body.interests) {
            setInitialNodes(TreeNodeHelper.mapNodes(response.body.interests));
          }
          setSubmitButtonDisabled(false);
          setIsNewsLetterModalVisible(true);
        } else if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
          ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
        } else {
          ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'error');
        }
      })
      .catch(error => {
        ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
        console.log(error);
      });
  };

  const onClickGenerateNewsletter = (newsletterId: number) => {
    Request.post(`${API.SCRAPER_SERVICE}/generateNewsletterContent`, {
      body: {
        newsletterId: newsletterId
      }
    })
      .then((response: any) => {
        if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
          ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
        } else if (response.status.toString() === HttpStatusCode.NOT_FOUND) {
          ToasterHelper.notify(t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_DOES_NOT_EXIST'), 'error');
        } else if (response.status.toString() === HttpStatusCode.OK) {
          ToasterHelper.notify(t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_GENERATION_REQUEST_SENT'), 'success');
        } else {
          ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'error');
        }
      })
      .catch(error => {
        ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
        console.log(error);
      });
  };

  const onNewsletterTextChange = (newsletterText: string) => {
    setNewsletterText(DOMPurify.sanitize(newsletterText));
  };

  return (
    <>
      {!token && <Redirect path='/back-office/signin' />}
      <div>
        <div className='main-div'>
          <ToastContainer />
          <div className='main-content'>
            <div className='col-md-12 flex block'>
              <div className='col-md-10'>
                <h4>{t('BACK_OFFICE.NEWSLETTERS.NEWSLETTERS_LIST')}</h4>
              </div>
              <div className='col-md-2 text-right text-sm'>
                <Button className='background button-border-radius cursor-pointer' onClick={openNewsLetterModal}>
                  <b>{t('BACK_OFFICE.NEWSLETTERS.CREATE_NEWSLETTER')}</b>
                </Button>
              </div>
            </div>
            <div className='col-md-12 flex mt-4'>
              <div className='col-md-1 text-sm mt-2'>{t('MODELS.COMPANY.NAME')}</div>
              <div className='col-md-7'>
                <Form.Group className='mb-6' controlId='name'>
                  <Form.Control type='text' onChange={validateNameSearch} placeholder={t('COMMON.NAME')} />
                </Form.Group>
              </div>
              <div className='col-md-3 text-sm text-right mt-2'>{t('COMMON.YEAR')}</div>
              <div className='col-md-1 text-sm text-right'>
                <select value={year} onChange={changeYear} className='border-1 rounded-1 text-sm min-h-8'>
                  {years.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className='margin-before-20px text-sm'>
              <DataTable value={rowData} tableStyle={{ minWidth: '50rem' }} stripedRows lazy>
                {columns.map(col => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    style={{ width: col.width }}
                    body={col.body}
                    alignHeader={col.header === 'Actions' ? 'center' : null}
                    hidden={col.hidden}
                  />
                ))}
              </DataTable>
              <Paginator
                first={pageNumber - 1}
                rows={pageSize}
                totalRecords={totalRows}
                rowsPerPageOptions={GRID.rowsPerPageOptions}
                onPageChange={onPageChange}
              />
            </div>
            <div className='card'>
              <Dialog
                header={!newsletterId ? t('BACK_OFFICE.NEWSLETTERS.CREATE_NEWSLETTER') : t('BACK_OFFICE.NEWSLETTERS.UPDATE_NEWSLETTER')}
                visible={isNewsLetterModalVisible}
                onHide={() => {
                  if (!isNewsLetterModalVisible) return;
                  setNewsletterId(0);
                  setNewsletterName('');
                  setNewsletterText('');
                  setInitialNodes(undefined);
                  setIsNewsLetterModalVisible(false);
                }}
                style={{ width: '75vw' }}
                breakpoints={{ '750px': '75vw', '641px': '100vw' }}>
                <Form noValidate validated={validated} onSubmit={!newsletterId ? onCreateNewsLetter : onUpdateNewsLetter}>
                  <div className='col-md-12 flex text-sm'>
                    <div className='col-md-2 mt-2'>{t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_NAME')}</div>
                    <div className='col-md-4'>
                      <Form.Group className='mb-6 text-sm' controlId='newsLetterName'>
                        <Form.Control
                          type='text'
                          required
                          value={newsletterName}
                          onChange={validateName}
                          placeholder={t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_NAME')}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <div className='col-md-12 flex text-sm'>
                    <div className='col-md-2'>{t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_TEXT')}</div>
                    <div className='col-md-10'>
                      <InputTextarea
                        id='newsLetterText'
                        required
                        value={DOMPurify.sanitize(newsletterText)}
                        onChange={e => onNewsletterTextChange(e.target.value)}
                        placeholder={t('BACK_OFFICE.NEWSLETTERS.NEWSLETTER_TEXT')}
                        rows={3}
                        cols={100}
                      />
                    </div>
                  </div>
                  <InterestsTreeTable
                    selectedNodeKeys={selectedNodeKeys}
                    onSelectionChange={setSelectedNodeKeys}
                    initialNodes={initialNodes}
                  />
                  <div className='col-md-12 text-right mt-4'>
                    <Button type='submit' className='background button-border-radius cursor-pointer' disabled={submitButtonDisabled}>
                      <b>{t('COMMON.SUBMIT')}</b>
                    </Button>
                  </div>
                </Form>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BackOfficeNewsletters;
